
import { Vue, Options } from "vue-class-component";
import DetailStatusSection from "@/app/ui/views/dashboard/cod-dashboard/component/detail-status-section.vue";
import {
  SummaryCodDashboardData,
  SummaryType
} from "@/domain/entities/Dashboard";
import { formatByCountry, resolveCountry } from "@/app/infrastructures/misc/Utils";
import { DashboardController } from "@/app/ui/controllers/DashboardController";

class DetailStatusSummary {
  statusName = "";
  statusSubName = "";
  toolTipDescription = "";
  sttDisplay = "";
  percentageSttDisplay = "";
  totalAmountSttDisplay = "";
  backgroundColor = "";
  iconColor = "";
  iconName = "";
  statusType = "";
  loading = false;
  detail: DetailStatusSummary[] = [];

  constructor(fields?: Partial<DetailStatusSummary>) {
    Object.assign(this, fields);
  }
}

@Options({
  components: { DetailStatusSection }
})
export default class OverallSummarySection extends Vue {
  mounted() {
    this.getLastSyncDataDashboard();
    this.fetchSummaryCodDashboard();
  }
  listOfSummaryPerStatus: SummaryCodDashboardData[] = [
    new SummaryCodDashboardData({
      sttStatus: "TOTAL",
      loading: true
    }),
    new SummaryCodDashboardData({
      sttStatus: "DEL-collected",
      loading: true
    }),
    new SummaryCodDashboardData({
      sttStatus: "POD-collected",
      loading: true,
      paymentMethod: "QRIS"
    }),
    new SummaryCodDashboardData({
      sttStatus: "POD-collected",
      loading: true,
      paymentMethod: "CASH"
    }),
    new SummaryCodDashboardData({
      sttStatus: "CODREJ-collected",
      loading: true
    }),
    new SummaryCodDashboardData({
      sttStatus: "DEX-collected",
      loading: true
    }),
    new SummaryCodDashboardData({
      sttStatus: "DEL-outstanding",
      loading: true
    }),
    new SummaryCodDashboardData({
      sttStatus: "POD-outstanding",
      loading: true
    }),
    new SummaryCodDashboardData({
      sttStatus: "CODREJ-outstanding",
      loading: true
    }),
    new SummaryCodDashboardData({
      sttStatus: "DEX-outstanding",
      loading: true
    })
  ];
  async fetchSummaryCodDashboard() {
    let delay = 0;
    const delayIncrement = 250;
    const requestSummaryCodDashboards = this.listOfSummaryPerStatus.map(
      (summary: SummaryCodDashboardData) => {
        delay += delayIncrement;
        return new Promise(resolve => {
          setTimeout(resolve, delay);
        }).then(() => {
          return DashboardController.getSummaryCodDashboardData(
            new SummaryType({
              sttStatus: summary.sttStatus,
              type: "courier",
              paymentMethod: summary.paymentMethod
            })
          ).then((resp: SummaryCodDashboardData) => {
            return resp;
          });
        });
      }
    );
    this.listOfSummaryPerStatus = await Promise.all(
      requestSummaryCodDashboards
    );
  }

  async getLastSyncDataDashboard() {
    const response = await DashboardController.getLastSyncCOD();
    const lastSyncCODDashboard = !response.lastSyncDate
      ? ""
      : this.$moment(response.lastSyncDate).format("DD MMMM YYYY, HH:mm");
    DashboardController.setLastSycnCODDashboard(lastSyncCODDashboard);
  }

  calculatePercentage(xNumber: number, yNumber: number): string {
    if (xNumber === 0 || yNumber === 0) return "0";
    return ((xNumber / yNumber) * 100).toFixed(2);
  }

  get totalDataCodDashboard() {
    const summaryData = this.listOfSummaryPerStatus.find(
      (summary: SummaryCodDashboardData) => summary.sttStatus === "TOTAL"
    );
    return summaryData ? summaryData : new SummaryCodDashboardData();
  }
  get dataDelCollectedCodDashboard() {
    const summaryData = this.listOfSummaryPerStatus.find(
      (summary: SummaryCodDashboardData) =>
        summary.sttStatus === "DEL-collected"
    );
    return summaryData ? summaryData : new SummaryCodDashboardData();
  }
  get dataPodCollectedCodQrisDashboard() {
    const summaryData = this.listOfSummaryPerStatus.find(
      (summary: SummaryCodDashboardData) =>
        summary.sttStatus === "POD-collected" &&
        summary.paymentMethod === "QRIS"
    );
    return summaryData ? summaryData : new SummaryCodDashboardData();
  }
  get dataPodCollectedCodCashDashboard() {
    const summaryData = this.listOfSummaryPerStatus.find(
      (summary: SummaryCodDashboardData) =>
        summary.sttStatus === "POD-collected" &&
        summary.paymentMethod === "CASH"
    );
    return summaryData ? summaryData : new SummaryCodDashboardData();
  }
  get dataDexCollectedCodDashboard() {
    const summaryData = this.listOfSummaryPerStatus.find(
      (summary: SummaryCodDashboardData) =>
        summary.sttStatus === "DEX-collected"
    );
    return summaryData ? summaryData : new SummaryCodDashboardData();
  }
  get dataCodRejCollectedCodDashboard() {
    const summaryData = this.listOfSummaryPerStatus.find(
      (summary: SummaryCodDashboardData) =>
        summary.sttStatus === "CODREJ-collected"
    );
    return summaryData ? summaryData : new SummaryCodDashboardData();
  }
  get dataDelOutstandingCodDashboard() {
    const summaryData = this.listOfSummaryPerStatus.find(
      (summary: SummaryCodDashboardData) =>
        summary.sttStatus === "DEL-outstanding"
    );
    return summaryData ? summaryData : new SummaryCodDashboardData();
  }
  get dataPodOutstandingCodDashboard() {
    const summaryData = this.listOfSummaryPerStatus.find(
      (summary: SummaryCodDashboardData) =>
        summary.sttStatus === "POD-outstanding"
    );
    return summaryData ? summaryData : new SummaryCodDashboardData();
  }
  get dataDexOutstandingCodDashboard() {
    const summaryData = this.listOfSummaryPerStatus.find(
      (summary: SummaryCodDashboardData) =>
        summary.sttStatus === "DEX-outstanding"
    );
    return summaryData ? summaryData : new SummaryCodDashboardData();
  }
  get dataCodRejOutstandingCodDashboard() {
    const summaryData = this.listOfSummaryPerStatus.find(
      (summary: SummaryCodDashboardData) =>
        summary.sttStatus === "CODREJ-outstanding"
    );
    return summaryData ? summaryData : new SummaryCodDashboardData();
  }

  get prefixCurrency() {
    return resolveCountry() === "MY" ? "RM" : "Rp";
  }

  get statusDelSection() {
    return new DetailStatusSummary({
      loading:
        this.dataDelCollectedCodDashboard.loading ||
        this.dataDelOutstandingCodDashboard.loading ||
        this.totalDataCodDashboard.loading,
      statusType: "DEL",
      iconName: "delivery",
      backgroundColor: "yellow-lp-400",
      iconColor: "yellow-lp-500",
      statusName: this.$t("codReconcile.report.inDeliveryProcess"),
      sttDisplay: this.dataDelCollectedCodDashboard.sttQuantity.toString(),
      totalAmountSttDisplay: formatByCountry(
        this.dataDelCollectedCodDashboard.sttTotalCodAmount, { type: "money", prefix: this.prefixCurrency }
      ),
      percentageSttDisplay: this.calculatePercentage(
        this.dataDelCollectedCodDashboard.sttQuantity,
        this.totalDataCodDashboard.sttQuantity
      ),
      toolTipDescription:
        "<ol style='list-style-type: decimal;'>" +
        `<li>${this.$t("codReconcile.report.tooltip.delStt")}</li>` +
        `<li><span class='text-red-lp-300'>${this.$t("codReconcile.report.tooltip.delPercentage")}</span></li>` +
        `<li><span class='text-green-lp-400'>${this.$t("codReconcile.report.tooltip.delValue")}</span></li>` +
        "</ol>"
    });
  }

  podDexData(type: "total" | "outstanding" | "collected") {
    const podData = {
      iconName: "pod-dex",
      backgroundColor: "green-lp-500",
      iconColor: "green-lp-400"
    };

    switch (type) {
      case "total":
        return new DetailStatusSummary({
          ...podData,
          loading:
            this.dataPodCollectedCodQrisDashboard.loading ||
            this.dataPodCollectedCodCashDashboard.loading ||
            this.totalDataCodDashboard.loading,
          statusName: this.$t("codReconcile.list.deliverySuccess"),
          sttDisplay: (
            this.dataPodCollectedCodQrisDashboard.sttQuantity +
            this.dataPodCollectedCodCashDashboard.sttQuantity +
            this.dataPodOutstandingCodDashboard.sttQuantity
          ).toString(),
          totalAmountSttDisplay: formatByCountry(
            this.dataPodCollectedCodQrisDashboard.sttTotalCodAmount +
              this.dataPodCollectedCodCashDashboard.sttTotalCodAmount +
              this.dataPodOutstandingCodDashboard.sttTotalCodAmount,
            { type: "money", prefix: this.prefixCurrency }
          ),
          percentageSttDisplay: this.calculatePercentage(
            this.dataPodCollectedCodQrisDashboard.sttQuantity +
              this.dataPodCollectedCodCashDashboard.sttQuantity +
              this.dataPodOutstandingCodDashboard.sttQuantity,
            this.totalDataCodDashboard.sttQuantity
          ),
          toolTipDescription:
            "<ol style='list-style-type: decimal;'>" +
            `<li><span class='text-green-lp-400'>${this.$t("codReconcile.report.tooltip.podTotalValue")}</span></li>` +
            `<li>${this.$t("codReconcile.report.tooltip.podTotalStt")}</li>` +
            `<li><span class='text-red-lp-300'>${this.$t("codReconcile.report.tooltip.podPercentage")}</span></li>` +
            "</ol>"
        });

      case "outstanding":
        return new DetailStatusSummary({
          ...podData,
          loading:
            this.dataPodOutstandingCodDashboard.loading ||
            this.totalDataCodDashboard.loading,
          statusType: "POD",
          statusName: this.$t("codReconcile.list.deliverySuccess"),
          statusSubName: this.$t("codReconcile.report.notYetDeposited"),
          sttDisplay: `${this.dataPodOutstandingCodDashboard.sttQuantity}/${this
            .dataPodCollectedCodQrisDashboard.sttQuantity +
            this.dataPodCollectedCodCashDashboard.sttQuantity +
            this.dataPodOutstandingCodDashboard.sttQuantity}`,
          totalAmountSttDisplay: formatByCountry(
            this.dataPodOutstandingCodDashboard.sttTotalCodAmount,
            { type: "money", prefix: this.prefixCurrency }
          ),
          percentageSttDisplay: this.calculatePercentage(
            this.dataPodOutstandingCodDashboard.sttQuantity,
            this.dataPodCollectedCodQrisDashboard.sttQuantity +
              this.dataPodCollectedCodCashDashboard.sttQuantity +
              this.dataPodOutstandingCodDashboard.sttQuantity
          ),
          toolTipDescription:
            "<ol style='list-style-type: decimal;'>" +
            `<li><span class='text-green-lp-400'>${this.$t("codReconcile.report.tooltip.podNotDepositedValue")}</span></li>` +
            `<li>${this.$t("codReconcile.report.tooltip.podNotDepositedStt")}</li>` +
            `<li><span class='text-red-lp-300'>${this.$t("codReconcile.report.tooltip.podNotDepositedPercentage")}</span></li>` +
            "</ol>"
        });

      case "collected":
        return new DetailStatusSummary({
          ...podData,
          loading:
            this.dataPodCollectedCodQrisDashboard.loading ||
            this.dataPodCollectedCodCashDashboard.loading ||
            this.totalDataCodDashboard.loading,
          statusName: this.$t("codReconcile.list.deliverySuccess"),
          statusSubName: this.$t("codReconcile.report.alreadyDeposited"),
          toolTipDescription:
            "<ol style='list-style-type: decimal;'>" +
            `<li><span class='text-green-lp-400'>${this.$t("codReconcile.report.tooltip.podDepositedValue")}</span></li>` +
            `<li>${this.$t("codReconcile.report.tooltip.podDepositedStt")}</li>` +
            `<li><span class='text-red-lp-300'>${this.$t("codReconcile.report.tooltip.podDepositedPercentage")}</span></li>` +
            "</ol>",
          detail: [
            new DetailStatusSummary({
              statusName: "QRIS",
              sttDisplay: `${
                this.dataPodCollectedCodQrisDashboard.sttQuantity
              }/${this.dataPodCollectedCodQrisDashboard.sttQuantity +
                this.dataPodCollectedCodCashDashboard.sttQuantity +
                this.dataPodOutstandingCodDashboard.sttQuantity}`,
              totalAmountSttDisplay: formatByCountry(
                this.dataPodCollectedCodQrisDashboard.sttTotalCodAmount,
                { type: "money", prefix: this.prefixCurrency }
              ),
              percentageSttDisplay: this.calculatePercentage(
                this.dataPodCollectedCodQrisDashboard.sttQuantity,
                this.dataPodCollectedCodQrisDashboard.sttQuantity +
                  this.dataPodCollectedCodCashDashboard.sttQuantity +
                  this.dataPodOutstandingCodDashboard.sttQuantity
              )
            }),
            new DetailStatusSummary({
              statusName: this.$t("codReconcile.report.cash"),
              sttDisplay: `${
                this.dataPodCollectedCodCashDashboard.sttQuantity
              }/${this.dataPodCollectedCodQrisDashboard.sttQuantity +
                this.dataPodCollectedCodCashDashboard.sttQuantity +
                this.dataPodOutstandingCodDashboard.sttQuantity}`,
              totalAmountSttDisplay: formatByCountry(
                this.dataPodCollectedCodCashDashboard.sttTotalCodAmount,
                { type: "money", prefix: this.prefixCurrency }
              ),
              percentageSttDisplay: this.calculatePercentage(
                this.dataPodCollectedCodCashDashboard.sttQuantity,
                this.dataPodCollectedCodQrisDashboard.sttQuantity +
                  this.dataPodCollectedCodCashDashboard.sttQuantity +
                  this.dataPodOutstandingCodDashboard.sttQuantity
              )
            })
          ]
        });
      default:
        break;
    }
  }

  codRejDexData(
    type: "total" | "codrej" | "collected-codrej" | "dex" | "collected-dex"
  ) {
    const codRejData = {
      iconName: "codrej-dex",
      backgroundColor: "red-lp-1100",
      iconColor: "red-lp-300"
    };

    switch (type) {
      case "total":
        return new DetailStatusSummary({
          ...codRejData,
          loading:
            this.totalDataCodDashboard.loading ||
            this.dataDexOutstandingCodDashboard.loading ||
            this.dataDexCollectedCodDashboard.loading ||
            this.dataCodRejOutstandingCodDashboard.loading ||
            this.dataCodRejCollectedCodDashboard.loading,
          statusName: this.$t("codReconcile.report.deliveryFailed"),
          sttDisplay: (
            this.dataDexOutstandingCodDashboard.sttQuantity +
            this.dataDexCollectedCodDashboard.sttQuantity +
            this.dataCodRejOutstandingCodDashboard.sttQuantity +
            this.dataCodRejCollectedCodDashboard.sttQuantity
          ).toString(),
          percentageSttDisplay: this.calculatePercentage(
            this.dataDexOutstandingCodDashboard.sttQuantity +
              this.dataDexCollectedCodDashboard.sttQuantity +
              this.dataCodRejOutstandingCodDashboard.sttQuantity +
              this.dataCodRejCollectedCodDashboard.sttQuantity,
            this.totalDataCodDashboard.sttQuantity
          ),
          toolTipDescription:
            "<ol style='list-style-type: decimal;'>" +
            `<li>${this.$t("codReconcile.report.tooltip.failedTotalStt")}</li>` +
            `<li><span class='text-red-lp-300'>${this.$t("codReconcile.report.tooltip.failedPercentage")}</span></li>` +
            "</ol>"
        });

      case "codrej":
        return new DetailStatusSummary({
          ...codRejData,
          loading:
            this.totalDataCodDashboard.loading ||
            this.dataCodRejOutstandingCodDashboard.loading,
          statusType: "CODREJ",
          statusName: this.$t("codReconcile.report.deliveryFailedCodrej"),
          statusSubName: this.$t("codReconcile.report.notYetDeposited"),
          sttDisplay: `${
            this.dataCodRejOutstandingCodDashboard.sttQuantity
          }/${this.dataCodRejOutstandingCodDashboard.sttQuantity +
            this.dataCodRejCollectedCodDashboard.sttQuantity +
            this.dataDexOutstandingCodDashboard.sttQuantity +
            this.dataDexCollectedCodDashboard.sttQuantity}`,
          totalAmountSttDisplay: "",
          percentageSttDisplay: this.calculatePercentage(
            this.dataCodRejOutstandingCodDashboard.sttQuantity,
            this.dataCodRejOutstandingCodDashboard.sttQuantity +
              this.dataCodRejCollectedCodDashboard.sttQuantity +
              this.dataDexOutstandingCodDashboard.sttQuantity +
              this.dataDexCollectedCodDashboard.sttQuantity
          ),
          toolTipDescription:
            "<ol style='list-style-type: decimal;'>" +
            `<li><span class='text-green-lp-400'>${this.$t("codReconcile.report.tooltip.codrejNotDepositedValue")}</span></li>` +
            `<li>${this.$t("codReconcile.report.tooltip.codrejNotDepositedStt")}</li>` +
            `<li><span class='text-red-lp-300'>${this.$t("codReconcile.report.tooltip.codrejNotDepositedPercentage")}</span></li>` +
            "</ol>"
        });

      case "collected-codrej":
        return new DetailStatusSummary({
          ...codRejData,
          loading:
            this.totalDataCodDashboard.loading ||
            this.dataCodRejCollectedCodDashboard.loading,
          statusName: this.$t("codReconcile.report.deliveryFailedCodrej"),
          statusSubName: this.$t("codReconcile.report.alreadyDeposited"),
          sttDisplay: `${
            this.dataCodRejCollectedCodDashboard.sttQuantity
          }/${this.dataCodRejCollectedCodDashboard.sttQuantity +
            this.dataCodRejOutstandingCodDashboard.sttQuantity +
            this.dataDexCollectedCodDashboard.sttQuantity +
            this.dataDexOutstandingCodDashboard.sttQuantity}`,
          percentageSttDisplay: this.calculatePercentage(
            this.dataCodRejCollectedCodDashboard.sttQuantity,
            this.dataCodRejCollectedCodDashboard.sttQuantity +
              this.dataCodRejOutstandingCodDashboard.sttQuantity +
              this.dataDexCollectedCodDashboard.sttQuantity +
              this.dataDexOutstandingCodDashboard.sttQuantity
          ),
          toolTipDescription:
            "<ol style='list-style-type: decimal;'>" +
            `<li><span class='text-green-lp-400'>${this.$t("codReconcile.report.tooltip.codrejDepositedValue")}</span></li>` +
            `<li>${this.$t("codReconcile.report.tooltip.codrejDepositedStt")}</li>` +
            `<li><span class='text-red-lp-300'>${this.$t("codReconcile.report.tooltip.codrejDepositedPercentage")}</span></li>` +
            "</ol>"
        });

      case "dex":
        return new DetailStatusSummary({
          ...codRejData,
          loading:
            this.totalDataCodDashboard.loading ||
            this.dataDexOutstandingCodDashboard.loading,
          statusType: "DEX",
          statusName: this.$t("codReconcile.report.deliveryFailedDex"),
          statusSubName: this.$t("codReconcile.report.notYetDeposited"),
          sttDisplay: `${this.dataDexOutstandingCodDashboard.sttQuantity}/${this
            .dataCodRejOutstandingCodDashboard.sttQuantity +
            this.dataCodRejCollectedCodDashboard.sttQuantity +
            this.dataDexOutstandingCodDashboard.sttQuantity +
            this.dataDexCollectedCodDashboard.sttQuantity}`,
          percentageSttDisplay: this.calculatePercentage(
            this.dataDexOutstandingCodDashboard.sttQuantity,
            this.dataCodRejOutstandingCodDashboard.sttQuantity +
              this.dataCodRejCollectedCodDashboard.sttQuantity +
              this.dataDexOutstandingCodDashboard.sttQuantity +
              this.dataDexCollectedCodDashboard.sttQuantity
          ),
          toolTipDescription:
            "<ol style='list-style-type: decimal;'>" +
            `<li><span class='text-green-lp-400'>${this.$t("codReconcile.report.tooltip.dexNotDepositedValue")}</span></li>` +
            `<li>${this.$t("codReconcile.report.tooltip.dexNotDepositedStt")}</li>` +
            `<li><span class='text-red-lp-300'>${this.$t("codReconcile.report.tooltip.dexNotDepositedPercentage")}</span></li>` +
            "</ol>"
        });

      case "collected-dex":
        return new DetailStatusSummary({
          ...codRejData,
          loading:
            this.totalDataCodDashboard.loading ||
            this.dataDexCollectedCodDashboard.loading,
          statusName: this.$t("codReconcile.report.deliveryFailedDex"),
          statusSubName: this.$t("codReconcile.report.alreadyDeposited"),
          sttDisplay: `${this.dataDexCollectedCodDashboard.sttQuantity}/${this
            .dataCodRejCollectedCodDashboard.sttQuantity +
            this.dataCodRejOutstandingCodDashboard.sttQuantity +
            this.dataDexCollectedCodDashboard.sttQuantity +
            this.dataDexOutstandingCodDashboard.sttQuantity}`,
          percentageSttDisplay: this.calculatePercentage(
            this.dataDexCollectedCodDashboard.sttQuantity,
            this.dataCodRejCollectedCodDashboard.sttQuantity +
              this.dataCodRejOutstandingCodDashboard.sttQuantity +
              this.dataDexCollectedCodDashboard.sttQuantity +
              this.dataDexOutstandingCodDashboard.sttQuantity
          ),
          toolTipDescription:
            "<ol style='list-style-type: decimal;'>" +
            `<li><span class='text-green-lp-400'>${this.$t("codReconcile.report.tooltip.dexDepositedValue")}</span></li>` +
            `<li>${this.$t("codReconcile.report.tooltip.dexDepositedStt")}</li>` +
            `<li><span class='text-red-lp-300'>${this.$t("codReconcile.report.tooltip.dexDepositedPercentage")}</span></li>` +
            "</ol>"
        });

      default:
        break;
    }
  }
}
